/**
 * The handshake between the device-authorisation tab and the page that opened it.
 *
 * The aggregator forbids authenticating inside an iframe or a WebView — providers
 * may block it outright — and the portal itself can be running at
 * `embed/patient-portal` inside a clinic's own site. So authorisation always
 * happens in a top-level tab, and the answer has to cross back to the page the
 * patient started from.
 *
 * **Two channels carry it, because `window.opener` is not dependable here.** A
 * provider whose sign-in page sends `Cross-Origin-Opener-Policy: same-origin`
 * moves the tab into a new browsing context group, and the browser severs
 * `opener` permanently — redirecting back to our own origin afterwards does not
 * restore it. A provider that opens its own window for sign-in breaks the
 * handshake a second way: the tab that finally lands here is then not the one
 * the opener holds a reference to, so an identity check on the sender rejects a
 * perfectly good answer. Either way the page that started the flow waits for a
 * message that never comes, and the connection appears only when the daily
 * sweep gets to it.
 *
 * So the outcome goes out over BOTH `postMessage` and a `BroadcastChannel`. The
 * channel needs neither an opener nor a window reference — only same-origin,
 * which it enforces itself — and it is what makes the handshake survive both
 * failures. `postMessage` is kept because it is the stricter of the two when it
 * works, and whichever arrives first wins.
 */
export const WEARABLE_CONNECT_MESSAGE = "wearable-connect";

/**
 * The broadcast channel for one clinic's connect outcomes.
 *
 * Scoped by tenant, which is as narrow as it can be: both sides have to derive
 * the same name with no round trip, and this tab knows the tenant from its own
 * path but not which patient the flow was started for — it is mounted outside
 * the console shell precisely so it holds no patient context. A nonce would be
 * narrower and is not available: the aggregator appends its own query parameters
 * and makes no promise about preserving ours, which is the same reason the
 * outcome itself travels in the path.
 *
 * Within a tenant, a subscriber only reacts while it has an attempt of its own
 * in flight, and the backend refuses a user id whose reference names a different
 * patient — so a second chart open in another tab cannot absorb this answer.
 */
export function wearableConnectChannelName(tenant: string): string {
  return `${WEARABLE_CONNECT_MESSAGE}:${tenant}`;
}

/**
 * Announce an outcome to whichever tab is waiting for it.
 *
 * Silent on failure by design. This runs on a page whose only job is to carry an
 * answer back, and a browser without `BroadcastChannel` — or one that throws
 * constructing it in a partitioned context — must still render the result and
 * let `postMessage` and the sweep do their work.
 */
export function publishWearableConnectOutcome(
  tenant: string,
  message: WearableConnectMessage,
): void {
  if (
    typeof window === "undefined" ||
    typeof BroadcastChannel === "undefined"
  ) {
    return;
  }
  try {
    const channel = new BroadcastChannel(wearableConnectChannelName(tenant));
    channel.postMessage(message);
    channel.close();
  } catch {
    // See above: never let the report break the page reporting it.
  }
}

/**
 * Listen for a connect outcome for this clinic. Returns an unsubscribe.
 *
 * No origin check here, unlike the `postMessage` path: a `BroadcastChannel` is
 * same-origin by construction and cannot deliver across origins, so there is
 * nothing to verify. What the caller must still do is ignore a message when it
 * has no attempt in flight — the channel carries no sender identity, so that is
 * the only thing standing in for `postMessage`'s source check.
 */
export function subscribeToWearableConnectOutcome(
  tenant: string,
  onOutcome: (message: WearableConnectMessage) => void,
): () => void {
  if (
    typeof window === "undefined" ||
    typeof BroadcastChannel === "undefined"
  ) {
    return () => undefined;
  }
  let channel: BroadcastChannel;
  try {
    channel = new BroadcastChannel(wearableConnectChannelName(tenant));
  } catch {
    return () => undefined;
  }
  const handler = (event: MessageEvent) => {
    if (!isWearableConnectMessage(event.data)) return;
    onOutcome(event.data);
  };
  channel.addEventListener("message", handler);
  return () => {
    channel.removeEventListener("message", handler);
    channel.close();
  };
}

export interface WearableConnectMessage {
  type: typeof WEARABLE_CONNECT_MESSAGE;
  ok: boolean;
  /**
   * The aggregator's id for the user it just authorised, lifted off the redirect.
   *
   * Carried back to the opener rather than acted on in the callback tab, because
   * the opener is the page that knows WHOSE readings these are — the patient,
   * from their own portal session. The callback tab knows only the tenant, off
   * its own path.
   *
   * Untrusted the whole way: the aggregator appends it to a redirect the browser
   * then controls, and the opener only forwards it to a backend that asks the
   * aggregator whose it really is. Optional because the aggregator makes no
   * promise about preserving query parameters, and its absence costs only speed —
   * the swept authorisation still lands.
   */
  terraUserId?: string;
  /**
   * Whether the callback tab itself managed to POST the confirmation.
   *
   * True only when the call was attempted AND resolved to something other than
   * the swallowed-error `null` — never merely "we tried". The opener uses this
   * to decide whether it still owes a POST of its own: sending one regardless
   * would ask the aggregator for the 90-day export a second time. Optional for
   * the same reason `terraUserId` is: older tabs, or one that bailed out before
   * reaching the report, still resolve to no field at all.
   */
  saved?: boolean;
}

/**
 * Whether a received message is our handshake.
 *
 * `ok` is checked for being a real boolean, not merely present: a malformed
 * `{ ok: "false" }` is truthy and would report a refused authorisation as a
 * success. `terraUserId` and `saved` are checked only for being the right type
 * when present, since nothing downstream trusts their contents anyway. Callers
 * must still verify the event's origin and source — this only says the payload
 * is the right shape.
 */
export function isWearableConnectMessage(
  data: unknown,
): data is WearableConnectMessage {
  if (typeof data !== "object" || data === null) return false;
  const message = data as Record<string, unknown>;
  if (message.type !== WEARABLE_CONNECT_MESSAGE) return false;
  if (typeof message.ok !== "boolean") return false;
  if (
    message.terraUserId !== undefined &&
    typeof message.terraUserId !== "string"
  ) {
    return false;
  }
  return message.saved === undefined || typeof message.saved === "boolean";
}

/**
 * "GARMIN" as the aggregator sends it, "Garmin" as a person reads it.
 *
 * Lives here rather than beside the connection card that first needed it: a
 * measurement value names its own device too, and the card is a client component
 * pulling the `@/components/ui` barrel and `react-icons`, none of which a string
 * formatter should drag into a chart.
 *
 * Falls back to the generic "Device" for a connection the aggregator never named
 * a provider for, and for a value written before values carried one.
 */
export function formatWearableProvider(provider: string | null): string {
  if (!provider) return "Device";
  return provider
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * How long a minted link has left, in words a patient can act on.
 *
 * Read off the session rather than stated as a constant: the aggregator returns
 * its own `expires_in`, which is a day today but only 15 minutes if a response
 * ever omits it and the fallback stands in. Telling a patient "24 hours" over a
 * quarter-hour link is the failure this avoids.
 */
function formatLinkLifetime(expiresInSeconds: number): string {
  const hours = Math.floor(expiresInSeconds / 3600);
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"}`;
  const minutes = Math.max(1, Math.round(expiresInSeconds / 60));
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/**
 * The text a clinic sends a patient to connect a device on their own phone.
 *
 * A fixed string, unlike the quote message's tenant-configurable template — that
 * needs a settings surface to author it, which this does not have yet.
 *
 * The last line is not filler. Messaging apps open links in an embedded browser,
 * and the aggregator forbids authorising inside one, so "paste it into your
 * browser" is the difference between a patient who connects and a patient who
 * reports that the link is broken.
 */
export function generateConnectLinkMessage(
  patientFirstName: string,
  session: { url: string; expiresInSeconds: number },
  clinicName: string | undefined,
): string {
  const clinic = clinicName || "your care team";
  return `Hi ${patientFirstName},

Connect your wearable or health app so your readings reach ${clinic}:

${session.url}

The link works for the next ${formatLinkLifetime(session.expiresInSeconds)}. If it doesn't open, paste it into your phone's browser.`;
}
